import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Shield, CreditCard, Clock, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button.jsx';

export default function FinalCTASection({ onJoinWaitlist }) {
  const navigate = useNavigate();
  const trustBadges = [
    { icon: Shield, text: 'Safe for kids' },
    { icon: CreditCard, text: 'No credit card required' },
    { icon: Clock, text: 'Cancel anytime' },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-[#7B68EE] via-[#9B8AC4] to-[#8B9FD9] relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white" />
        <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-white" />
        <div className="absolute top-1/2 left-1/3 w-24 h-24 rounded-full bg-white" />
      </div>

      {/* Floating Stars */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute text-white/20"
          style={{
            top: `${15 + Math.random() * 70}%`,
            left: `${5 + Math.random() * 90}%`,
          }}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 180, 360],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: i * 0.4,
          }}
        >
          <Star className="w-6 h-6" fill="currentColor" />
        </motion.div>
      ))}

      {/* Sparkle decorations */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={`sparkle-${i}`}
          className="absolute text-white/30"
          style={{
            top: `${20 + Math.random() * 60}%`,
            left: `${10 + Math.random() * 80}%`,
          }}
          animate={{
            opacity: [0.3, 1, 0.3],
            scale: [0.8, 1.3, 0.8],
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            repeat: Infinity,
            delay: i * 0.5,
          }}
        >
          <Sparkles className="w-5 h-5" />
        </motion.div>
      ))}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 font-display drop-shadow-lg">
            Ready to Create Magic?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of families discovering the joy of personalized, mindful storytelling.
          </p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
        >
          <Button
            onClick={() => navigate('/wizard')}
            className="bg-white text-[#7B68EE] hover:bg-[#F8F6FB] px-8 py-4 text-lg rounded-full shadow-xl transition-transform transform hover:scale-105 font-semibold"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            Start Creating Stories
          </Button>
          <Button
            onClick={onJoinWaitlist}
            variant="outline"
            className="border-2 border-white/50 text-white hover:bg-white/10 px-8 py-4 text-lg rounded-full backdrop-blur-sm"
          >
            Join the Waitlist
          </Button>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-6"
        >
          {trustBadges.map((badge, index) => {
            const IconComponent = badge.icon;
            return (
              <motion.div
                key={index}
                className="flex items-center gap-2 text-white/90 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full"
                whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                <IconComponent className="w-4 h-4" />
                <span className="text-sm font-medium">{badge.text}</span>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
